import { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Textarea } from '../../components/ui/textarea';
import { 
  Calendar,
  Clock,
  Mail,
  Send,
  Plus,
  Play,
  Pause,
  Trophy,
  BarChart2,
  RefreshCw,
  Trash2,
  Eye,
  Beaker,
  CheckCircle,
  XCircle,
  ArrowRight,
  Upload,
  FileSpreadsheet,
  Download,
  AlertTriangle,
  TrendingUp,
  Activity
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminEmailSchedulingPage() {
  const { token } = useAuth();
  const [activeTab, setActiveTab] = useState('scheduled');
  const fileInputRef = useRef(null);
  
  // Scheduled emails state
  const [scheduledEmails, setScheduledEmails] = useState([]);
  const [emailStats, setEmailStats] = useState({ pending: 0, sent: 0, failed: 0 });
  const [loadingEmails, setLoadingEmails] = useState(false);
  
  // Batches state
  const [batches, setBatches] = useState([]);
  const [loadingBatches, setLoadingBatches] = useState(false);
  const [showNewBatch, setShowNewBatch] = useState(false);
  const [newBatch, setNewBatch] = useState({ name: '', template_type: '', scheduled_for: '' });
  const [uploadingBatchId, setUploadingBatchId] = useState(null);
  const [csvPreview, setCsvPreview] = useState(null);
  
  // Tracking state
  const [trackingStats, setTrackingStats] = useState(null);
  
  // A/B Tests state
  const [abTests, setAbTests] = useState([]);
  const [loadingTests, setLoadingTests] = useState(false);
  const [showNewTest, setShowNewTest] = useState(false);
  const [selectedTest, setSelectedTest] = useState(null);
  const [newTest, setNewTest] = useState({
    name: '',
    template_type: 'enrollment_confirmation',
    variant_a_subject: '',
    variant_a_html: '',
    variant_b_subject: '',
    variant_b_html: '',
    split_percentage: 50,
    winning_metric: 'open_rate',
  });
  
  // Templates for dropdown
  const [templates, setTemplates] = useState([]);

  useEffect(() => {
    fetchScheduledEmails();
    fetchBatches();
    fetchABTests();
    fetchTemplates();
    fetchTrackingStats();
  }, []);

  const fetchTemplates = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/email-templates/`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setTemplates(data.templates || []);
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  };

  const fetchTrackingStats = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/email-templates/tracking/stats/`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setTrackingStats(data);
      }
    } catch (error) {
      console.error('Error fetching tracking stats:', error);
    }
  };

  const fetchScheduledEmails = async () => {
    setLoadingEmails(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/email-templates/scheduling/emails/`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setScheduledEmails(data.emails || []);
        setEmailStats(data.stats || { pending: 0, sent: 0, failed: 0 });
      }
    } catch (error) {
      console.error('Error fetching scheduled emails:', error);
    } finally {
      setLoadingEmails(false);
    }
  };

  const fetchBatches = async () => {
    setLoadingBatches(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/email-templates/scheduling/batches/`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setBatches(data.batches || []);
      }
    } catch (error) {
      console.error('Error fetching batches:', error);
    } finally {
      setLoadingBatches(false);
    }
  };

  const fetchABTests = async () => {
    setLoadingTests(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/email-templates/ab-tests/`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setAbTests(data.tests || []);
      }
    } catch (error) {
      console.error('Error fetching A/B tests:', error);
    } finally {
      setLoadingTests(false);
    }
  };

  const createBatch = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/email-templates/scheduling/batches/create/`, {
        method: 'POST',
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newBatch),
      });
      const data = await response.json();
      if (data.success) {
        toast.success('Batch created successfully');
        setShowNewBatch(false);
        setNewBatch({ name: '', template_type: '', scheduled_for: '' });
        fetchBatches();
      } else {
        toast.error(data.error || 'Failed to create batch');
      }
    } catch (error) {
      toast.error('Failed to create batch');
    }
  };

  const uploadCsvToBatch = async (batchId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await fetch(`${API_URL}/api/admin/email-templates/scheduling/batches/${batchId}/upload-csv/`, {
        method: 'POST',
        headers: { 
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });
      const data = await response.json();
      if (data.success) {
        toast.success(`Added ${data.stats?.valid_recipients || 0} recipients to batch`);
        if (data.errors?.length > 0) {
          toast.warning(`${data.errors.length} rows had errors`);
        }
        fetchBatches();
        fetchScheduledEmails();
      } else {
        toast.error(data.message || 'Failed to upload CSV');
      }
    } catch (error) {
      toast.error('Failed to upload CSV');
    }
    setUploadingBatchId(null);
  };

  const downloadCsvTemplate = async (templateType) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/email-templates/csv/template/?template_type=${templateType}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        // Create and download file
        const blob = new Blob([data.csv_content], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `email_recipients_${templateType}.csv`;
        a.click();
        window.URL.revokeObjectURL(url);
        toast.success('CSV template downloaded');
      }
    } catch (error) {
      toast.error('Failed to download template');
    }
  };

  const enableAutoWinner = async (testId, enabled) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/email-templates/ab-tests/${testId}/auto-winner/`, {
        method: 'POST',
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          enabled, 
          confidence_level: 0.95,
          min_sample_size: 100 
        }),
      });
      const data = await response.json();
      if (data.success) {
        toast.success(data.message);
        fetchABTests();
      }
    } catch (error) {
      toast.error('Failed to update auto-winner setting');
    }
  };

  const createABTest = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/email-templates/ab-tests/create/`, {
        method: 'POST',
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newTest),
      });
      const data = await response.json();
      if (data.success) {
        toast.success('A/B Test created successfully');
        setShowNewTest(false);
        setNewTest({
          name: '',
          template_type: 'enrollment_confirmation',
          variant_a_subject: '',
          variant_a_html: '',
          variant_b_subject: '',
          variant_b_html: '',
          split_percentage: 50,
          winning_metric: 'open_rate',
        });
        fetchABTests();
      } else {
        toast.error(data.error || 'Failed to create test');
      }
    } catch (error) {
      toast.error('Failed to create A/B test');
    }
  };

  const startTest = async (testId) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/email-templates/ab-tests/${testId}/start/`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      if (data.success) {
        toast.success('Test started');
        fetchABTests();
      }
    } catch (error) {
      toast.error('Failed to start test');
    }
  };

  const pauseTest = async (testId) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/email-templates/ab-tests/${testId}/pause/`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      if (data.success) {
        toast.success('Test paused');
        fetchABTests();
      }
    } catch (error) {
      toast.error('Failed to pause test');
    }
  };

  const completeTest = async (testId) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/email-templates/ab-tests/${testId}/complete/`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      if (data.success) {
        toast.success(`Test completed! Winner: Variant ${data.winner}`);
        fetchABTests();
      }
    } catch (error) {
      toast.error('Failed to complete test');
    }
  };

  const simulateResults = async (testId) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/email-templates/ab-tests/${testId}/simulate/`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      if (data.success) {
        toast.success('Simulated results added');
        fetchABTests();
      }
    } catch (error) {
      toast.error('Failed to simulate results');
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      pending: 'secondary',
      sent: 'default',
      failed: 'destructive',
      cancelled: 'outline',
      draft: 'secondary',
      scheduled: 'default',
      processing: 'default',
      completed: 'default',
      running: 'default',
      paused: 'secondary',
    };
    return <Badge variant={variants[status] || 'outline'}>{status}</Badge>;
  };

  return (
    <div className="space-y-6" data-testid="email-scheduling-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-foreground flex items-center gap-2">
            <Calendar className="h-7 w-7 text-primary" />
            Email Scheduling & A/B Testing
          </h1>
          <p className="text-muted-foreground mt-1">
            Schedule emails, create batches, and run A/B tests
          </p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="scheduled" data-testid="tab-scheduled">
            <Clock className="h-4 w-4 mr-2" />
            Scheduled
          </TabsTrigger>
          <TabsTrigger value="batches" data-testid="tab-batches">
            <Mail className="h-4 w-4 mr-2" />
            Batches
          </TabsTrigger>
          <TabsTrigger value="abtests" data-testid="tab-abtests">
            <Beaker className="h-4 w-4 mr-2" />
            A/B Tests
          </TabsTrigger>
        </TabsList>

        {/* Scheduled Emails Tab */}
        <TabsContent value="scheduled" className="space-y-4">
          {/* Stats Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Card>
              <CardContent className="p-4 flex items-center gap-4">
                <div className="h-12 w-12 rounded-full bg-amber-100 flex items-center justify-center">
                  <Clock className="h-6 w-6 text-amber-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Pending</p>
                  <h3 className="text-2xl font-bold text-amber-600">{emailStats.pending}</h3>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 flex items-center gap-4">
                <div className="h-12 w-12 rounded-full bg-green-100 flex items-center justify-center">
                  <CheckCircle className="h-6 w-6 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Sent</p>
                  <h3 className="text-2xl font-bold text-green-600">{emailStats.sent}</h3>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 flex items-center gap-4">
                <div className="h-12 w-12 rounded-full bg-red-100 flex items-center justify-center">
                  <XCircle className="h-6 w-6 text-red-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Failed</p>
                  <h3 className="text-2xl font-bold text-red-600">{emailStats.failed}</h3>
                </div>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Scheduled Emails</CardTitle>
                <CardDescription>Emails queued for sending</CardDescription>
              </div>
              <Button variant="outline" onClick={fetchScheduledEmails}>
                <RefreshCw className={`h-4 w-4 mr-2 ${loadingEmails ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </CardHeader>
            <CardContent>
              {scheduledEmails.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Mail className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No scheduled emails</p>
                  <p className="text-sm">Emails will appear here when you create batches or schedule individual sends</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {scheduledEmails.map((email) => (
                    <div key={email.id} className="flex items-center justify-between p-3 border rounded-lg">
                      <div>
                        <p className="font-medium">{email.recipient_email}</p>
                        <p className="text-sm text-muted-foreground">
                          {email.template_type} • {new Date(email.scheduled_for).toLocaleString()}
                        </p>
                      </div>
                      {getStatusBadge(email.status)}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Batches Tab */}
        <TabsContent value="batches" className="space-y-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Email Batches</CardTitle>
                <CardDescription>Group emails for bulk sending</CardDescription>
              </div>
              <Button onClick={() => setShowNewBatch(true)}>
                <Plus className="h-4 w-4 mr-2" />
                New Batch
              </Button>
            </CardHeader>
            <CardContent>
              {showNewBatch && (
                <Card className="mb-4 border-primary">
                  <CardContent className="p-4 space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <Label>Batch Name</Label>
                        <Input
                          placeholder="e.g., January Newsletter"
                          value={newBatch.name}
                          onChange={(e) => setNewBatch(prev => ({...prev, name: e.target.value}))}
                        />
                      </div>
                      <div>
                        <Label>Template</Label>
                        <select
                          className="w-full h-10 px-3 rounded-md border border-input bg-background"
                          value={newBatch.template_type}
                          onChange={(e) => setNewBatch(prev => ({...prev, template_type: e.target.value}))}
                        >
                          <option value="">Select template...</option>
                          {templates.map((t) => (
                            <option key={t.template_type} value={t.template_type}>{t.name}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <Label>Scheduled For</Label>
                        <Input
                          type="datetime-local"
                          value={newBatch.scheduled_for}
                          onChange={(e) => setNewBatch(prev => ({...prev, scheduled_for: e.target.value}))}
                        />
                      </div>
                    </div>
                    <div className="flex gap-2 justify-end">
                      <Button variant="outline" onClick={() => setShowNewBatch(false)}>Cancel</Button>
                      <Button onClick={createBatch}>Create Batch</Button>
                    </div>
                  </CardContent>
                </Card>
              )}

              {batches.length === 0 && !showNewBatch ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Mail className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No email batches</p>
                  <p className="text-sm">Create a batch to send emails to multiple recipients at once</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {batches.map((batch) => (
                    <div key={batch.id} className="p-4 border rounded-lg">
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <p className="font-medium">{batch.name}</p>
                          <p className="text-sm text-muted-foreground">
                            {batch.template_type} • {batch.total_emails} recipients • {new Date(batch.scheduled_for).toLocaleString()}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          {getStatusBadge(batch.status)}
                        </div>
                      </div>
                      
                      {/* CSV Upload Section */}
                      {batch.status === 'draft' && (
                        <div className="flex items-center gap-2 pt-3 border-t">
                          <input
                            type="file"
                            accept=".csv"
                            className="hidden"
                            id={`csv-upload-${batch.id}`}
                            onChange={(e) => {
                              if (e.target.files?.[0]) {
                                uploadCsvToBatch(batch.id, e.target.files[0]);
                              }
                            }}
                          />
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => document.getElementById(`csv-upload-${batch.id}`).click()}
                          >
                            <Upload className="h-4 w-4 mr-1" />
                            Upload CSV
                          </Button>
                          <Button 
                            size="sm" 
                            variant="ghost"
                            onClick={() => downloadCsvTemplate(batch.template_type)}
                          >
                            <Download className="h-4 w-4 mr-1" />
                            Download Template
                          </Button>
                          <div className="flex-1" />
                          <Button size="sm" variant="default">
                            <Send className="h-4 w-4 mr-1" />
                            Schedule
                          </Button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* How Batches Work */}
          <Card>
            <CardHeader>
              <CardTitle>How Email Batches Work</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-4 gap-4 text-sm">
                <div className="text-center">
                  <div className="h-10 w-10 rounded-full bg-primary text-white flex items-center justify-center mx-auto mb-2">1</div>
                  <p className="font-medium">Create Batch</p>
                  <p className="text-muted-foreground">Name it and select a template</p>
                </div>
                <div className="text-center">
                  <div className="h-10 w-10 rounded-full bg-primary text-white flex items-center justify-center mx-auto mb-2">2</div>
                  <p className="font-medium">Add Recipients</p>
                  <p className="text-muted-foreground">Upload a CSV or select from your contacts</p>
                </div>
                <div className="text-center">
                  <div className="h-10 w-10 rounded-full bg-primary text-white flex items-center justify-center mx-auto mb-2">3</div>
                  <p className="font-medium">Schedule</p>
                  <p className="text-muted-foreground">Choose when to send</p>
                </div>
                <div className="text-center">
                  <div className="h-10 w-10 rounded-full bg-primary text-white flex items-center justify-center mx-auto mb-2">4</div>
                  <p className="font-medium">Track Results</p>
                  <p className="text-muted-foreground">Monitor delivery and opens</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* A/B Tests Tab */}
        <TabsContent value="abtests" className="space-y-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>A/B Tests</CardTitle>
                <CardDescription>Compare email variants to optimize performance</CardDescription>
              </div>
              <Button onClick={() => setShowNewTest(true)}>
                <Plus className="h-4 w-4 mr-2" />
                New A/B Test
              </Button>
            </CardHeader>
            <CardContent>
              {showNewTest && (
                <Card className="mb-4 border-primary">
                  <CardContent className="p-4 space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label>Test Name</Label>
                        <Input
                          placeholder="e.g., Subject Line Test - February"
                          value={newTest.name}
                          onChange={(e) => setNewTest(prev => ({...prev, name: e.target.value}))}
                        />
                      </div>
                      <div>
                        <Label>Template Type</Label>
                        <select
                          className="w-full h-10 px-3 rounded-md border border-input bg-background"
                          value={newTest.template_type}
                          onChange={(e) => setNewTest(prev => ({...prev, template_type: e.target.value}))}
                        >
                          {templates.map((t) => (
                            <option key={t.template_type} value={t.template_type}>{t.name}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <Card>
                        <CardHeader className="pb-2">
                          <CardTitle className="text-sm flex items-center gap-2">
                            <Badge>A</Badge> Control Variant
                          </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-2">
                          <Input
                            placeholder="Subject line for Variant A"
                            value={newTest.variant_a_subject}
                            onChange={(e) => setNewTest(prev => ({...prev, variant_a_subject: e.target.value}))}
                          />
                          <Textarea
                            placeholder="HTML content (or paste from template)"
                            rows={4}
                            value={newTest.variant_a_html}
                            onChange={(e) => setNewTest(prev => ({...prev, variant_a_html: e.target.value}))}
                          />
                        </CardContent>
                      </Card>
                      <Card>
                        <CardHeader className="pb-2">
                          <CardTitle className="text-sm flex items-center gap-2">
                            <Badge variant="secondary">B</Badge> Test Variant
                          </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-2">
                          <Input
                            placeholder="Subject line for Variant B"
                            value={newTest.variant_b_subject}
                            onChange={(e) => setNewTest(prev => ({...prev, variant_b_subject: e.target.value}))}
                          />
                          <Textarea
                            placeholder="HTML content (or paste from template)"
                            rows={4}
                            value={newTest.variant_b_html}
                            onChange={(e) => setNewTest(prev => ({...prev, variant_b_html: e.target.value}))}
                          />
                        </CardContent>
                      </Card>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label>Split Percentage (Variant B)</Label>
                        <Input
                          type="number"
                          min="10"
                          max="90"
                          value={newTest.split_percentage}
                          onChange={(e) => setNewTest(prev => ({...prev, split_percentage: parseInt(e.target.value)}))}
                        />
                        <p className="text-xs text-muted-foreground mt-1">
                          {100 - newTest.split_percentage}% will receive Variant A, {newTest.split_percentage}% will receive Variant B
                        </p>
                      </div>
                      <div>
                        <Label>Winning Metric</Label>
                        <select
                          className="w-full h-10 px-3 rounded-md border border-input bg-background"
                          value={newTest.winning_metric}
                          onChange={(e) => setNewTest(prev => ({...prev, winning_metric: e.target.value}))}
                        >
                          <option value="open_rate">Open Rate</option>
                          <option value="click_rate">Click Rate</option>
                        </select>
                      </div>
                    </div>

                    <div className="flex gap-2 justify-end">
                      <Button variant="outline" onClick={() => setShowNewTest(false)}>Cancel</Button>
                      <Button onClick={createABTest}>Create Test</Button>
                    </div>
                  </CardContent>
                </Card>
              )}

              {abTests.length === 0 && !showNewTest ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Beaker className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No A/B tests</p>
                  <p className="text-sm">Create a test to compare different email variants</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {abTests.map((test) => (
                    <Card key={test.id} className={test.status === 'running' ? 'border-green-500' : ''}>
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between mb-4">
                          <div>
                            <h3 className="font-medium flex items-center gap-2">
                              {test.name}
                              {test.winner && (
                                <Badge className="bg-amber-500">
                                  <Trophy className="h-3 w-3 mr-1" />
                                  Winner: {test.winner}
                                </Badge>
                              )}
                            </h3>
                            <p className="text-sm text-muted-foreground">{test.template_type}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            {getStatusBadge(test.status)}
                            {test.status === 'draft' && (
                              <>
                                <Button size="sm" variant="outline" onClick={() => simulateResults(test.id)}>
                                  <BarChart2 className="h-4 w-4 mr-1" />
                                  Simulate
                                </Button>
                                <Button size="sm" onClick={() => startTest(test.id)}>
                                  <Play className="h-4 w-4 mr-1" />
                                  Start
                                </Button>
                              </>
                            )}
                            {test.status === 'running' && (
                              <>
                                <Button size="sm" variant="outline" onClick={() => simulateResults(test.id)}>
                                  <BarChart2 className="h-4 w-4 mr-1" />
                                  Simulate
                                </Button>
                                <Button size="sm" variant="outline" onClick={() => pauseTest(test.id)}>
                                  <Pause className="h-4 w-4 mr-1" />
                                  Pause
                                </Button>
                                <Button size="sm" onClick={() => completeTest(test.id)}>
                                  <Trophy className="h-4 w-4 mr-1" />
                                  Pick Winner
                                </Button>
                              </>
                            )}
                            {test.status === 'paused' && (
                              <Button size="sm" onClick={() => startTest(test.id)}>
                                <Play className="h-4 w-4 mr-1" />
                                Resume
                              </Button>
                            )}
                          </div>
                        </div>

                        {/* Results Grid */}
                        <div className="grid grid-cols-2 gap-4">
                          <div className={`p-4 rounded-lg border ${test.winner === 'A' ? 'border-green-500 bg-green-50' : 'bg-muted/30'}`}>
                            <div className="flex items-center justify-between mb-2">
                              <Badge>Variant A</Badge>
                              {test.winner === 'A' && <Trophy className="h-4 w-4 text-amber-500" />}
                            </div>
                            <p className="text-xs text-muted-foreground truncate mb-2">{test.variant_a_subject || 'No subject set'}</p>
                            <div className="grid grid-cols-3 gap-2 text-center">
                              <div>
                                <p className="text-lg font-bold">{test.variant_a_sent}</p>
                                <p className="text-xs text-muted-foreground">Sent</p>
                              </div>
                              <div>
                                <p className="text-lg font-bold text-blue-600">{test.variant_a_open_rate}%</p>
                                <p className="text-xs text-muted-foreground">Open Rate</p>
                              </div>
                              <div>
                                <p className="text-lg font-bold text-green-600">{test.variant_a_click_rate}%</p>
                                <p className="text-xs text-muted-foreground">Click Rate</p>
                              </div>
                            </div>
                          </div>
                          <div className={`p-4 rounded-lg border ${test.winner === 'B' ? 'border-green-500 bg-green-50' : 'bg-muted/30'}`}>
                            <div className="flex items-center justify-between mb-2">
                              <Badge variant="secondary">Variant B</Badge>
                              {test.winner === 'B' && <Trophy className="h-4 w-4 text-amber-500" />}
                            </div>
                            <p className="text-xs text-muted-foreground truncate mb-2">{test.variant_b_subject || 'No subject set'}</p>
                            <div className="grid grid-cols-3 gap-2 text-center">
                              <div>
                                <p className="text-lg font-bold">{test.variant_b_sent}</p>
                                <p className="text-xs text-muted-foreground">Sent</p>
                              </div>
                              <div>
                                <p className="text-lg font-bold text-blue-600">{test.variant_b_open_rate}%</p>
                                <p className="text-xs text-muted-foreground">Open Rate</p>
                              </div>
                              <div>
                                <p className="text-lg font-bold text-green-600">{test.variant_b_click_rate}%</p>
                                <p className="text-xs text-muted-foreground">Click Rate</p>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Statistical Significance */}
                        {test.significance && (
                          <div className="mt-4 p-3 rounded-lg bg-muted/50 border">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <Activity className="h-4 w-4 text-primary" />
                                <span className="font-medium text-sm">Statistical Analysis</span>
                              </div>
                              {test.significance.is_significant ? (
                                <Badge className="bg-green-500">
                                  <CheckCircle className="h-3 w-3 mr-1" />
                                  Statistically Significant
                                </Badge>
                              ) : (
                                <Badge variant="secondary">
                                  <AlertTriangle className="h-3 w-3 mr-1" />
                                  Not Yet Significant
                                </Badge>
                              )}
                            </div>
                            <div className="grid grid-cols-4 gap-4 mt-3 text-sm">
                              <div>
                                <p className="text-muted-foreground text-xs">P-Value</p>
                                <p className="font-medium">{test.significance.p_value?.toFixed(4) || 'N/A'}</p>
                              </div>
                              <div>
                                <p className="text-muted-foreground text-xs">Z-Score</p>
                                <p className="font-medium">{test.significance.z_score?.toFixed(2) || 'N/A'}</p>
                              </div>
                              <div>
                                <p className="text-muted-foreground text-xs">Confidence</p>
                                <p className="font-medium">{((test.significance.confidence_level || 0.95) * 100).toFixed(0)}%</p>
                              </div>
                              <div>
                                <p className="text-muted-foreground text-xs">Lift</p>
                                <p className="font-medium">
                                  {test.significance.lift ? (
                                    <span className={test.significance.lift > 0 ? 'text-green-600' : 'text-red-600'}>
                                      {test.significance.lift > 0 ? '+' : ''}{test.significance.lift}%
                                    </span>
                                  ) : 'N/A'}
                                </p>
                              </div>
                            </div>
                            {!test.auto_select_winner && test.status === 'running' && (
                              <div className="mt-3 pt-3 border-t">
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  onClick={() => enableAutoWinner(test.id, true)}
                                >
                                  <TrendingUp className="h-4 w-4 mr-1" />
                                  Enable Auto-Winner (95% confidence)
                                </Button>
                              </div>
                            )}
                            {test.auto_select_winner && (
                              <p className="mt-2 text-xs text-muted-foreground">
                                Auto-winner is enabled. Winner will be selected automatically when statistical significance is reached.
                              </p>
                            )}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* A/B Testing Guide */}
          <Card>
            <CardHeader>
              <CardTitle>A/B Testing Best Practices</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 gap-6 text-sm">
                <div>
                  <h4 className="font-semibold mb-2">What to Test</h4>
                  <ul className="space-y-1 text-muted-foreground">
                    <li>• <strong>Subject Lines:</strong> Try different lengths, emojis, personalization</li>
                    <li>• <strong>Call-to-Action:</strong> Button text, color, placement</li>
                    <li>• <strong>Send Times:</strong> Morning vs evening, weekday vs weekend</li>
                    <li>• <strong>Content Length:</strong> Short vs detailed emails</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold mb-2">Tips for Success</h4>
                  <ul className="space-y-1 text-muted-foreground">
                    <li>• Test one variable at a time for clear results</li>
                    <li>• Use at least 100 recipients per variant</li>
                    <li>• Run tests for 24-48 hours before picking a winner</li>
                    <li>• Apply winning variants to future campaigns</li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
